import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken, decodeToken } from '../../utils/authHelpers';
import { authFetch } from '../../utils/authFetch';
import MentionTextarea from './MentionTextarea';
import './BoardComments.sass';

// Helper to escape special regex characters in a string
const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Build a profile URL from user type and profile IDs
const buildProfileUrl = ({ userType, creatorProfileId, hostProfileId, handle }) => {
  if (userType === 'creator' && creatorProfileId) {
    return `/profile?creator_id=${creatorProfileId}`;
  } else if (userType === 'host' && hostProfileId) {
    return `/profile?host_id=${hostProfileId}`;
  } else if (handle) {
    return `/profile?handle=${handle}`;
  }
  return '/profile';
};

// Render comment body with red, clickable mentions
// Uses exact handle matching to support handles with dots and special characters
const renderCommentBody = (body, mentions = []) => {
  if (!mentions || mentions.length === 0) {
    return body;
  }

  // Build a map of handles (with @ prefix, lowercased) to their mention data
  const mentionMap = {};
  mentions.forEach(m => {
    const handle = m.handle.startsWith('@') ? m.handle : `@${m.handle}`;
    mentionMap[handle.toLowerCase()] = m;
  });

  // Get all known mention handles (with @ prefix)
  const handles = Object.keys(mentionMap);
  if (handles.length === 0) {
    return body;
  }

  // Build a regex that matches any of the exact handles (case-insensitive)
  // Sort by length descending so longer handles match first (e.g., @chelsea.schillizzi before @chelsea)
  const sortedHandles = handles.sort((a, b) => b.length - a.length);
  const escapedHandles = sortedHandles.map(h => escapeRegExp(h));
  const mentionRegex = new RegExp(`(${escapedHandles.join('|')})`, 'gi');

  // Split body by the mention regex - this preserves the matched parts
  const parts = body.split(mentionRegex);

  // Map parts: if a part matches a known handle, wrap it in a link
  return parts.map((part, index) => {
    const lowerPart = part.toLowerCase();
    const mention = mentionMap[lowerPart];

    if (mention) {
      const profileUrl = buildProfileUrl({
        userType: mention.userType,
        creatorProfileId: mention.profileId,
        hostProfileId: mention.profileId,
        handle: mention.handle
      });

      return (
        <a
          key={index}
          href={profileUrl}
          className="comment-mention-link"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }

    return part;
  });
};

const BoardComments = ({ 
  target, // { kind: 'collab_property' | 'collab_creator' | 'travel_post', id: uuid }
  postOwnerUserId,
  isOpen = true,
  focusCommentId = null
}) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [mentions, setMentions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editMentions, setEditMentions] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [replyingToId, setReplyingToId] = useState(null); // root comment id
  const [replyText, setReplyText] = useState('');
  const [replyMentions, setReplyMentions] = useState([]);
  const [expandedThreads, setExpandedThreads] = useState({}); // { [rootCommentId]: true }

  const DEFAULT_VISIBLE_REPLIES = 2;

  // Get current user ID
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      const decoded = decodeToken(token);
      if (decoded?.userId) {
        setCurrentUserId(decoded.userId);
      }
    }
  }, []);

  // Fetch comments
  const fetchComments = useCallback(async () => {
    if (!target || !target.kind || !target.id || !isOpen) return;

    setLoading(true);
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      let email = '';
      try { email = localStorage.getItem('postaway_email') || ''; } catch (_) {}

      const res = await fetch(`${base}/api/board/comments/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, target })
      });

      const data = await res.json();
      if (data.ok) {
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  }, [target, isOpen]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Scroll to and highlight the focused comment after comments load
  useEffect(() => {
    if (!focusCommentId || loading || comments.length === 0) return;

    // Use a short timeout to ensure DOM is rendered
    const timer = setTimeout(() => {
      const element = document.getElementById(`comment-${focusCommentId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('board-comment-highlighted');
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          element.classList.remove('board-comment-highlighted');
        }, 3000);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [focusCommentId, loading, comments]);

  // Add comment
  const handleAddComment = async () => {
    if (!newComment.trim() || submitting) return;

    const token = getAuthToken();
    if (!token) {
      alert('Please log in to comment');
      return;
    }

    setSubmitting(true);
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const res = await authFetch(`${base}/api/board/comments/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target,
          body: newComment.trim(),
          mentions: mentions.map(m => ({ userId: m.userId, handle: m.handle }))
        })
      });

      const data = await res.json();
      if (data.ok) {
        setNewComment('');
        setMentions([]);
        await fetchComments();
      } else {
        alert('Failed to add comment');
      }
    } catch (err) {
      if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
        console.error('Error adding comment:', err);
        alert('Failed to add comment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Add reply (one-level thread)
  const handleAddReply = async (parentCommentId) => {
    if (!replyText.trim() || submitting) return;

    const token = getAuthToken();
    if (!token) {
      alert('Please log in to reply');
      return;
    }

    setSubmitting(true);
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const res = await authFetch(`${base}/api/board/comments/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target, // ignored by API when parentCommentId is present (inherits from parent)
          parentCommentId,
          body: replyText.trim(),
          mentions: replyMentions.map(m => ({ userId: m.userId, handle: m.handle }))
        })
      });

      const data = await res.json();
      if (data.ok) {
        setReplyingToId(null);
        setReplyText('');
        setReplyMentions([]);
        await fetchComments();
      } else {
        alert('Failed to add reply');
      }
    } catch (err) {
      if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
        console.error('Error adding reply:', err);
        alert('Failed to add reply');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Edit comment
  const handleEditComment = async (commentId) => {
    if (!editText.trim() || submitting) return;

    const token = getAuthToken();
    if (!token) {
      alert('Please log in');
      return;
    }

    setSubmitting(true);
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const res = await authFetch(`${base}/api/board/comments/edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          commentId,
          body: editText.trim(),
          mentions: editMentions.map(m => ({ userId: m.userId, handle: m.handle }))
        })
      });

      const data = await res.json();
      if (data.ok) {
        setEditingId(null);
        setEditText('');
        setEditMentions([]);
        await fetchComments();
      } else {
        alert('Failed to edit comment');
      }
    } catch (err) {
      if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
        console.error('Error editing comment:', err);
        alert('Failed to edit comment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    const token = getAuthToken();
    if (!token) {
      alert('Please log in');
      return;
    }

    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const res = await authFetch(`${base}/api/board/comments/delete?commentId=${commentId}`, {
        method: 'DELETE'
      });

      const data = await res.json();
      if (data.ok) {
        await fetchComments();
      } else {
        alert('Failed to delete comment');
      }
    } catch (err) {
      if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
        console.error('Error deleting comment:', err);
        alert('Failed to delete comment');
      }
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Check if user can delete (author or post owner)
  const canDelete = (comment) => {
    if (!currentUserId) return false;
    return comment.userId === currentUserId || postOwnerUserId === currentUserId;
  };

  // Check if user can edit (author only)
  const canEdit = (comment) => {
    if (!currentUserId) return false;
    return comment.userId === currentUserId;
  };

  if (!isOpen) return null;

  // Thread comments: root comments + repliesByParentId
  const repliesByParentId = {};
  comments.forEach((c) => {
    if (c.parentCommentId) {
      if (!repliesByParentId[c.parentCommentId]) {
        repliesByParentId[c.parentCommentId] = [];
      }
      repliesByParentId[c.parentCommentId].push(c);
    }
  });

  const rootComments = comments.filter(c => !c.parentCommentId);

  const toggleThreadExpanded = (rootId, expanded) => {
    setExpandedThreads(prev => ({ ...prev, [rootId]: expanded }));
  };

  const startReply = (rootId) => {
    const token = getAuthToken();
    if (!token) {
      alert('Please log in to reply');
      return;
    }
    setReplyingToId(rootId);
    setReplyText('');
    setReplyMentions([]);
    // Expand thread so the user sees context if it was collapsed
    toggleThreadExpanded(rootId, true);
  };

  return (
    <div className="board-comments">
      <div className="board-comments-header">
        <h4>Comments ({comments.length})</h4>
      </div>

      {/* Comment list */}
      <div className="board-comments-list">
        {loading && comments.length === 0 ? (
          <div className="board-comments-loading">Loading comments...</div>
        ) : comments.length === 0 ? (
          <div className="board-comments-empty">No comments yet. Be the first to comment!</div>
        ) : (
          rootComments.map((root) => {
            const threadReplies = repliesByParentId[root.id] || [];
            const isExpanded = !!expandedThreads[root.id];
            const visibleReplies = isExpanded ? threadReplies : threadReplies.slice(0, DEFAULT_VISIBLE_REPLIES);
            const hiddenCount = Math.max(0, threadReplies.length - visibleReplies.length);

            const renderSingleComment = (comment, { isReply }) => {
              const showActions = (!isReply) || canEdit(comment) || canDelete(comment);

              return (
                <div
                  key={comment.id}
                  id={`comment-${comment.id}`}
                  className={`board-comment${isReply ? ' board-comment--reply' : ''}`}
                >
                  <div className="board-comment-avatar">
                    <img 
                      src={comment.author.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.author.firstName || 'U')}&background=random&size=32`}
                      alt={comment.author.firstName || 'User'}
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.author.firstName || 'U')}&background=random&size=32`;
                      }}
                    />
                  </div>
                  <div className="board-comment-content">
                    <div className="board-comment-header">
                      <a 
                        href={buildProfileUrl({
                          userType: comment.author.userType,
                          creatorProfileId: comment.author.creatorProfileId,
                          hostProfileId: comment.author.hostProfileId,
                          handle: comment.author.handle
                        })}
                        className="board-comment-author-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {comment.author.handle ? `@${comment.author.handle}` : comment.author.firstName || 'User'}
                      </a>
                      <span className="board-comment-time">{formatTime(comment.createdAt)}</span>
                      {comment.updatedAt !== comment.createdAt && (
                        <span className="board-comment-edited">(edited)</span>
                      )}
                    </div>
                    
                    {editingId === comment.id ? (
                      <div className="board-comment-edit-form">
                        <MentionTextarea
                          value={editText}
                          onChange={setEditText}
                          onMentionsChange={setEditMentions}
                          placeholder="Edit your comment..."
                        />
                        <div className="board-comment-edit-actions">
                          <button 
                            className="board-comment-btn-cancel"
                            onClick={() => {
                              setEditingId(null);
                              setEditText('');
                              setEditMentions([]);
                            }}
                          >
                            Cancel
                          </button>
                          <button 
                            className="board-comment-btn-save"
                            onClick={() => handleEditComment(comment.id)}
                            disabled={submitting || !editText.trim()}
                          >
                            {submitting ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="board-comment-body">
                          {renderCommentBody(comment.body, comment.mentions)}
                        </div>
                        
                        {showActions && (
                          <div className="board-comment-actions">
                            {!isReply && (
                              <button
                                className="board-comment-btn-reply"
                                onClick={() => startReply(comment.id)}
                              >
                                Reply
                              </button>
                            )}
                            {canEdit(comment) && (
                              <button 
                                className="board-comment-btn-edit"
                                onClick={() => {
                                  setEditingId(comment.id);
                                  setEditText(comment.body);
                                  setEditMentions(comment.mentions || []);
                                }}
                              >
                                Edit
                              </button>
                            )}
                            {canDelete(comment) && (
                              <button 
                                className="board-comment-btn-delete"
                                onClick={() => handleDeleteComment(comment.id)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            };

            return (
              <div key={root.id} className="board-comment-thread">
                {renderSingleComment(root, { isReply: false })}

                {replyingToId === root.id && (
                  <div className="board-comment-reply-form">
                    <MentionTextarea
                      value={replyText}
                      onChange={setReplyText}
                      onMentionsChange={setReplyMentions}
                      placeholder="Write a reply... Use @ to mention someone"
                      disabled={submitting}
                    />
                    {replyText.includes('@') && replyMentions.length === 0 && (
                      <div className="board-comments-mention-hint">
                        Tip: Select a user from the dropdown when typing @ to notify them.
                      </div>
                    )}
                    <div className="board-comment-reply-actions">
                      <button
                        className="board-comment-btn-cancel"
                        onClick={() => {
                          setReplyingToId(null);
                          setReplyText('');
                          setReplyMentions([]);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="board-comment-btn-save"
                        onClick={() => handleAddReply(root.id)}
                        disabled={submitting || !replyText.trim()}
                      >
                        {submitting ? 'Posting...' : 'Post Reply'}
                      </button>
                    </div>
                  </div>
                )}

                {threadReplies.length > 0 && (
                  <div className="board-comment-replies">
                    {visibleReplies.map((reply) => renderSingleComment(reply, { isReply: true }))}

                    {threadReplies.length > DEFAULT_VISIBLE_REPLIES && !isExpanded && hiddenCount > 0 && (
                      <button
                        className="board-comment-btn-toggle-replies"
                        onClick={() => toggleThreadExpanded(root.id, true)}
                      >
                        View more replies ({hiddenCount})
                      </button>
                    )}

                    {threadReplies.length > DEFAULT_VISIBLE_REPLIES && isExpanded && (
                      <button
                        className="board-comment-btn-toggle-replies"
                        onClick={() => toggleThreadExpanded(root.id, false)}
                      >
                        Hide replies
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add comment form */}
      <div className="board-comments-form">
        <MentionTextarea
          value={newComment}
          onChange={setNewComment}
          onMentionsChange={setMentions}
          placeholder="Add a comment... Use @ to mention someone"
          disabled={submitting}
        />
        {/* Hint when @ is typed but no mention was selected from dropdown */}
        {newComment.includes('@') && mentions.length === 0 && (
          <div className="board-comments-mention-hint">
            Tip: Select a user from the dropdown when typing @ to notify them.
          </div>
        )}
        <button 
          className="board-comments-submit"
          onClick={handleAddComment}
          disabled={submitting || !newComment.trim()}
        >
          {submitting ? 'Posting...' : 'Post Comment'}
        </button>
      </div>
    </div>
  );
};

export default BoardComments;
